import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useLoading } from "../../contexts/LoadingContext";

import StructuredData from "./components/StructuredData";
import SeoMeta from "../../components/SeoMeta";
import RecipeTitle from "./components/RecipeTitle";
import Ingredients from "./components/Ingredients";
import Instructions from "./components/Instructions";
import WinePairing from "./components/WinePairing";
import NutritionalFacts from "./components/NutritionalFacts";


// Get locale from cookie
const getLocale = () => {
  const match = document.cookie.match(/(^|;)\s*locale=([^;]*)/);
  return match ? decodeURIComponent(match[2]) : "en";
};

function Recipe() {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const { setIsLoading } = useLoading();
  const locale = getLocale();
  const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || "http://localhost:1337";
  const STRAPI_TOKEN = process.env.REACT_APP_STRAPI_TOKEN;

  useEffect(() => {
    const fetchData = async () => {
      if (!STRAPI_URL || !STRAPI_TOKEN) return;

      const path = `/api/recipes?filters[slug][$eq]=${slug}&locale=${locale}&populate[ingredients][populate]=*&populate[instructions][populate]=*&populate[nutrition][populate]=*&populate[winePairing][populate]=*&populate[recipe_categories][populate]=*&populate[recipe_cuisines][populate]=*&populate[recipe_occasions][populate]=*&populate[cooking_styles][populate]=*&populate[cooking_techniques][populate]=*&populate[dietary_preferences][populate]=*&populate[recipe_courses][populate]=*&populate[image][populate]=*&populate[seo][populate]=*`;

      const url = new URL(STRAPI_URL + path);
		
		
	  const getPlainText = (richText) => {
	  if (!Array.isArray(richText)) return "";
	  return richText.map(block => block?.children?.map(child => child.text).join(" ")).join("\n");
	};

      setIsLoading(true);
      try {
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${STRAPI_TOKEN}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const result = await response.json();
          setPageData(result.data?.[0] || null);
        }
      } catch (err) {
        console.error("❌ Fetch error:", err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [slug, locale, STRAPI_URL, STRAPI_TOKEN, setIsLoading]); // ✅ stable dependencies

  if (!pageData) return null;

  return (
    <>
					  
		<StructuredData
		  title={pageData?.title}
		  description={pageData?.description?.map(block => block.children.map(child => child.text).join(" ")).join("\n")}
		  prepTime={pageData?.prepTime}
		  cookTime={pageData?.cookTime}
		  totalTime={pageData?.totalTime}
		  recipeYield={pageData?.recipeYield}
		  keywords={pageData?.keywords}
		  ingredients={pageData?.ingredients}
		  instructions={pageData?.instructions}
		  nutrition={pageData?.nutrition}
		  imageUrl={pageData?.image?.formats?.large?.url || pageData?.image?.url}
		/>								  

        <SeoMeta
		  lang={locale}
		  title={pageData?.seo?.Title || pageData?.title}
		  description={pageData?.seo?.Description || pageData?.description}
		  keywords={pageData?.seo?.keywords}
		  robots={pageData?.seo?.robots || "index, follow"}
		  imageUrl={pageData?.seo?.image?.formats?.large?.url || pageData?.seo?.image?.url}
		/>

      <section className="py-15 py-xl-20">
        <RecipeTitle
          title={pageData?.title}
          description={pageData?.description}
          prepTime={pageData?.prepTime}
          cookTime={pageData?.cookTime}
          totalTime={pageData?.totalTime}
          recipeYield={pageData?.recipeYield}
          difficulty={pageData?.difficulty}
          recipe_categories={pageData?.recipe_categories}
          recipe_cuisines={pageData?.recipe_cuisines}
          recipe_occasions={pageData?.recipe_occasions}
          cooking_styles={pageData?.cooking_styles}
          cooking_techniques={pageData?.cooking_techniques}
          dietary_preferences={pageData?.dietary_preferences}
          recipe_courses={pageData?.recipe_courses}
          image={pageData?.image}
        />
        <Ingredients ingredients={pageData?.ingredients} />
        <Instructions instructions={pageData?.instructions} />
        <WinePairing
          wineGeneralRecommendation={pageData?.wineGeneralRecommendation}
          winePairing={pageData?.winePairing}
        />
        <NutritionalFacts nutrition={pageData?.nutrition} />
      </section>
    </>
  );
}

export default Recipe;
